import { useEffect, useState, type FormEvent } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/services/api';
import { useApplicants } from '@/contexts/ApplicantContext';
import { Card } from '@/components/ui/Card';

interface BreakdownRow {
  source: string | null;
  status: string | null;
  c: number;
}

export default function QueuePage() {
  const { current } = useApplicants();
  const [stats, setStats] = useState<unknown>(null);
  const [statsErr, setStatsErr] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [urlsText, setUrlsText] = useState('');

  async function load() {
    setLoading(true);
    try {
      const [s, b] = await Promise.allSettled([
        api.get('/queue/stats'),
        api.get<{ breakdown: BreakdownRow[] }>(`/queue/breakdown?applicant_id=${current}`),
      ]);
      if (s.status === 'fulfilled') {
        setStats(s.value);
        setStatsErr(null);
      } else {
        setStats(null);
        setStatsErr(s.reason instanceof ApiError ? s.reason.message : String(s.reason));
      }
      if (b.status === 'fulfilled') setBreakdown(b.value.breakdown);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  async function addUrls(e: FormEvent) {
    e.preventDefault();
    const urls = urlsText
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (!urls.length) return;
    try {
      const r = await api.post<{ submitted: number }>('/queue/jobs', {
        urls,
        applicant_id: current,
      });
      setUrlsText('');
      toast.success(`Queued ${r.submitted} URL${r.submitted > 1 ? 's' : ''}`);
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'add_failed');
    }
  }

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-semibold tracking-tight">Queue</h2>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Broker stats">
          {statsErr ? (
            <p className="text-sm text-amber-700">
              Broker unreachable: <code className="text-xs">{statsErr}</code>
              <br />
              <span className="text-xs text-gray-500">
                Start it with <code>start_broker.bat</code> on HOST_A.
              </span>
            </p>
          ) : stats ? (
            <pre className="overflow-auto rounded-md bg-gray-50 p-3 text-xs">
              {JSON.stringify(stats, null, 2)}
            </pre>
          ) : (
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          )}
        </Card>

        <Card title="Add URLs (one per line)">
          <form onSubmit={addUrls} className="space-y-2">
            <textarea
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              placeholder={'https://boards.greenhouse.io/example/jobs/123\nhttps://jobs.lever.co/foo/abc'}
              rows={5}
              className="input font-mono text-xs"
            />
            <button type="submit" className="btn-primary">
              <Plus className="h-4 w-4" />
              Add to queue
            </button>
          </form>
        </Card>
      </div>

      <Card title={`Breakdown — ${current}`}>
        {loading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
        {!loading && breakdown.length === 0 && (
          <p className="text-sm text-gray-500">No jobs in scope.</p>
        )}
        {breakdown.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="py-1.5">Source</th>
                <th className="py-1.5">Status</th>
                <th className="py-1.5 text-right">Count</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((r, i) => (
                <tr key={i} className="border-t border-gray-50">
                  <td className="py-1.5 font-medium text-gray-700">{r.source ?? '—'}</td>
                  <td className="py-1.5 text-gray-600">{r.status ?? '—'}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.c}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
